import { ViteReactSSG } from 'vite-react-ssg'
import type { RouteRecord } from 'vite-react-ssg'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { Outlet, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import './index.css'

// Critical pages - loaded immediately
import Index from './pages/Index'
import NotFound from './pages/NotFound'

// Lazy load non-critical pages for code splitting
const Precios = lazy(() => import('./pages/Precios'))
const AsistenteIA = lazy(() => import('./pages/AsistenteIA'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogArticle = lazy(() => import('./pages/BlogArticle'))
const Herramientas = lazy(() => import('./pages/Herramientas'))
const CalculadoraISR = lazy(() => import('./pages/CalculadoraISR'))
const CalculadoraIVA = lazy(() => import('./pages/CalculadoraIVA'))
const CalculadoraRESICO = lazy(() => import('./pages/CalculadoraRESICO'))
const Contacto = lazy(() => import('./pages/Contacto'))
const Empresas = lazy(() => import('./pages/Empresas'))
const RESICO = lazy(() => import('./pages/RESICO'))
const ActividadEmpresarial = lazy(() => import('./pages/ActividadEmpresarial'))
const Arrendamiento = lazy(() => import('./pages/Arrendamiento'))
const PlataformasTecnologicas = lazy(() => import('./pages/PlataformasTecnologicas'))
const Regularizacion = lazy(() => import('./pages/Regularizacion'))
const Registro = lazy(() => import('./pages/Registro'))
const Referidos = lazy(() => import('./pages/Referidos'))
const ReferidosConfirmado = lazy(() => import('./pages/ReferidosConfirmado'))
const ReferidosDashboard = lazy(() => import('./pages/ReferidosDashboard'))
const Glosario = lazy(() => import('./pages/Glosario'))
const GlosarioTermino = lazy(() => import('./pages/GlosarioTermino'))
const AvisoPrivacidad = lazy(() => import('./pages/AvisoPrivacidad'))
const TerminosCondiciones = lazy(() => import('./pages/TerminosCondiciones'))
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'))
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'))
const AdminBlogEditor = lazy(() => import('./pages/admin/AdminBlogEditor'))
const AdminPagos = lazy(() => import('./pages/admin/AdminPagos'))
const Pagar = lazy(() => import('./pages/Pagar'))
const OAuthConsent = lazy(() => import('./pages/OAuthConsent'))

import { glosarioTerms } from './data/glosarioTerms'

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
)

// Wrapper for lazy components
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
)

const queryClient = new QueryClient()

// Root layout with providers
function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  )
}

// Define routes for SSG
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Index /> },
      { path: 'precios', element: <LazyWrapper><Precios /></LazyWrapper> },
      { path: 'asistente-ia', element: <LazyWrapper><AsistenteIA /></LazyWrapper> },
      { path: 'blog', element: <LazyWrapper><Blog /></LazyWrapper> },
      { 
        path: 'blog/:slug', 
        element: <LazyWrapper><BlogArticle /></LazyWrapper>,
      },
      { path: 'herramientas', element: <LazyWrapper><Herramientas /></LazyWrapper> },
      { path: 'herramientas/calculadora-isr', element: <LazyWrapper><CalculadoraISR /></LazyWrapper> },
      { path: 'herramientas/calculadora-iva', element: <LazyWrapper><CalculadoraIVA /></LazyWrapper> },
      { path: 'herramientas/calculadora-resico', element: <LazyWrapper><CalculadoraRESICO /></LazyWrapper> },
      { path: 'contacto', element: <LazyWrapper><Contacto /></LazyWrapper> },
      { path: 'empresas', element: <LazyWrapper><Empresas /></LazyWrapper> },
      { path: 'resico', element: <LazyWrapper><RESICO /></LazyWrapper> },
      { path: 'actividad-empresarial', element: <LazyWrapper><ActividadEmpresarial /></LazyWrapper> },
      { path: 'arrendamiento', element: <LazyWrapper><Arrendamiento /></LazyWrapper> },
      { path: 'plataformas-tecnologicas', element: <LazyWrapper><PlataformasTecnologicas /></LazyWrapper> },
      { path: 'regularizacion', element: <LazyWrapper><Regularizacion /></LazyWrapper> },
      { path: 'registro', element: <LazyWrapper><Registro /></LazyWrapper> },
      { path: 'referidos', element: <LazyWrapper><Referidos /></LazyWrapper> },
      { path: 'referidos/confirmado', element: <LazyWrapper><ReferidosConfirmado /></LazyWrapper> },
      { path: 'referidos/dashboard', element: <LazyWrapper><ReferidosDashboard /></LazyWrapper> },
      { path: 'pagar', element: <LazyWrapper><Pagar /></LazyWrapper> },
      { path: 'login', element: <Index /> },
      { path: 'glosario', element: <LazyWrapper><Glosario /></LazyWrapper> },
      { 
        path: 'glosario/:slug', 
        element: <LazyWrapper><GlosarioTermino /></LazyWrapper>,
        getStaticPaths: () => glosarioTerms.map(term => `glosario/${term.slug}`),
      },
      { path: 'aviso-privacidad', element: <LazyWrapper><AvisoPrivacidad /></LazyWrapper> },
      { path: 'terminos-condiciones', element: <LazyWrapper><TerminosCondiciones /></LazyWrapper> },
      { path: 'admin', element: <Navigate to="/admin/login" replace /> },
      { path: 'admin/login', element: <LazyWrapper><AdminLogin /></LazyWrapper> },
      { path: 'admin/blog', element: <LazyWrapper><AdminBlog /></LazyWrapper> },
      { path: 'admin/blog/nuevo', element: <LazyWrapper><AdminBlogEditor /></LazyWrapper> },
      { path: 'admin/pagos', element: <LazyWrapper><AdminPagos /></LazyWrapper> },
      { path: '.lovable/oauth/consent', element: <LazyWrapper><OAuthConsent /></LazyWrapper> },
      { 
        path: 'admin/blog/:id/editar', 
        element: <LazyWrapper><AdminBlogEditor /></LazyWrapper>,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]

export const createRoot = ViteReactSSG(
  { routes },
  () => {
    // Setup callback
  },
)
